import { ControlledInput } from "@dune/ui";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetSocietyInfoFromSirenNumber } from "../../../../api/queries/siren.queries";

interface SirenInputProps {
	name: string;
	label: string;
	placeholder?: string;
	onSirenValidated?: (societyData: unknown) => void;
}

export const SirenInput = ({
	name,
	label,
	placeholder = "Numéro SIREN (9 chiffres)",
	onSirenValidated,
}: SirenInputProps) => {
	const { control, formState, setValue, clearErrors } = useFormContext();
	const { errors } = formState;

	const sirenValue = useWatch({
		control,
		name,
	});

	const {
		data: societyData,
		isSuccess: isSirenSuccess,
		isError: isSirenError,
		error: sirenError,
	} = useGetSocietyInfoFromSirenNumber(sirenValue);

	const sirenAxiosError =
		isSirenError && isAxiosError(sirenError) ? sirenError.response?.data : null;

	useEffect(() => {
		if (isSirenSuccess && societyData) {
			setValue("denomination", societyData.denomination || "");

			if (societyData.headOfficeAddress) {
				const address = societyData.headOfficeAddress;
				setValue("city", address.city || "");
				setValue("streetName", address.streetName || "");
				setValue("streetNumber", address.streetNumber || "");
				setValue("postalCode", address.postalCode || "");
			}

			if (onSirenValidated) {
				onSirenValidated(societyData);
			}

			clearErrors(["siren", "denomination"]);
		}
	}, [societyData, isSirenSuccess, setValue, clearErrors, onSirenValidated]);

	const isValidSirenFormat = (value: string) => {
		return /^\d{9}$/.test(value);
	};

	const getError = () => {
		if (errors[name]?.message) {
			return errors[name]?.message?.toString();
		}

		if (sirenValue && !isValidSirenFormat(sirenValue)) {
			return "Le SIREN doit contenir exactement 9 chiffres";
		}

		if (sirenAxiosError) {
			return "SIREN non trouvé ou invalide";
		}

		return undefined;
	};

	return (
		<div className="space-y-2">
			<ControlledInput
				control={control}
				name={name}
				label={label}
				placeholder={placeholder}
				error={getError()}
				maxLength={9}
			/>
			{isSirenSuccess && societyData && (
				<div className="text-sm text-green-600">
					✓ Entreprise trouvée: {societyData.denomination}
				</div>
			)}
		</div>
	);
};
